import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import { Action, AnyAction, Dispatch, Middleware, MiddlewareAPI } from "redux";
import SuiteApi from "../api/SuiteApi";
import config from "../config";
import { ModuleDiagnosticInfo } from "../Model/Diagnostic";
import { AutoFocusCompleted, LocateDropCompleted } from "../Model/Efi";
import { ENotificationType } from "../Model/ENotificationType";
import { HistogramData } from "../Model/HistogramData";
import { ScheduleEntry } from "../Model/ImagingSchedule";
import { ModuleState } from "../Model/ModuleState";
import { PlateAddress } from "../Model/PlateAddress";
import { ModuleTemperatureChange } from "../Model/Temperature";
import { ChillerError } from "../Model/Chiller";
import { updateDiagnostic } from "./actions/DiagnosticActions";
import {
  queuedPlatesUpdated,
  scheduledPlatesUpdated,
} from "./actions/ImagingScheduleActions";
import { setHistogramData } from "./actions/LiveImageAction";
import {
  updateModuleAutoFocusState,
  updateModuleLocateDropState,
  updateModuleState,
  updateModuleStates,
} from "./actions/ModuleStateActions";
import {
  removeNotification,
  sendNotification,
} from "./actions/NotificationAction";
import { updatePlateAddresses } from "./actions/PlateAddressAction";
import { updateTemperatureReading } from "./actions/TemperatureActions";
import { LicenseInfo } from "../Model/License";
import { licenseAction } from "./actions/LicenseAction";
import { fetchStorageConfigs } from "../components/FetchStorageConfigs";
import { fetchPlateAddresses } from "../components/FetchPlateAddresses";

function startSignalRConnection(
  sendErrorNotification: boolean,
  connection: HubConnection,
  dispatch: Dispatch<AnyAction>,
  callbackFn?: () => void
) {
  connection
    .start()
    .then(() => {
      console.info("SignalR Connected");
      dispatch(removeNotification());
      callbackFn?.();
    })
    .catch((err) => {
      if (sendErrorNotification) {
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "SignalR Connection Error: " + err
          )
        );
      }
      setTimeout(() => {
        console.log("SignalR connection restarted");
        startSignalRConnection(false, connection, dispatch, () => {
          dispatch(fetchStorageConfigs(false) as any);
          dispatch(fetchPlateAddresses(false) as any);
        });
      }, 5000);
    });
}

const signalRMiddleware: Middleware =
  (api: MiddlewareAPI) => (next: Dispatch) => (action: Action<string>) => {
    if (action.type === "FETCH_MODULES_PENDING") {
      const url = config.SIGNALR_URL + "/push";

      // create the connection instance
      const connection = new HubConnectionBuilder().withUrl(url).build();

      const OnModuleStatusChanged = (moduleStates: ModuleState[]) => {
        api.dispatch(updateModuleStates(moduleStates));
      };

      const OnModuleStateChanged = (moduleState: ModuleState) => {
        api.dispatch(updateModuleState(moduleState));
      };

      const OnTemperatureChanged = (
        moduleTemperatureChanges: ModuleTemperatureChange[]
      ) => {
        api.dispatch(updateTemperatureReading(moduleTemperatureChanges));
      };

      const OnChillerModuleError = (ChillerError: ChillerError) => {
        api.dispatch(sendNotification(ChillerError.type, ChillerError.details));
      };

      const OnPlateAddressesUpdated = (plateAddresses: PlateAddress[]) => {
        api.dispatch(updatePlateAddresses(plateAddresses));
      };

      const OnScheduledPlatesUpdated = (imagingSchedules: ScheduleEntry[]) => {
        api.dispatch(scheduledPlatesUpdated(imagingSchedules));
      };

      const OnQueuedPlatesUpdated = (imagingSchedules: ScheduleEntry[]) => {
        api.dispatch(queuedPlatesUpdated(imagingSchedules));
      };

      const OnLocateDropCompleted = (
        locateDropCompletedEventArgs: LocateDropCompleted
      ) => {
        api.dispatch(updateModuleLocateDropState(locateDropCompletedEventArgs));
      };

      const OnAutoFocusCompleted = (
        autoFocusCompletedEventArgs: AutoFocusCompleted
      ) => {
        api.dispatch(updateModuleAutoFocusState(autoFocusCompletedEventArgs));
      };

      const OnModuleDiagnosticFinished = (
        diagnosticResult: ModuleDiagnosticInfo
      ) => {
        api.dispatch(updateDiagnostic(diagnosticResult));
      };

      const OnHistogramUpdated = (histogramData: HistogramData) => {
        api.dispatch(setHistogramData(histogramData));
      };

      const OnNotifyUser = (type: ENotificationType, message: string) => {
        api.dispatch(sendNotification(type, message));
      };

      const OnUserLoggedIn = () => {
        setTimeout(() => {
          SuiteApi.getStates();
        }, 1000);
      };

      const OnLicenseExpired = (data: { licenseInfo: LicenseInfo }) => {
        api.dispatch(
          licenseAction.updateLicenseInfo(data.licenseInfo.isAllowToRun)
        );
        if (data.licenseInfo.isAllowToRun === false) localStorage.clear();
      };

      // event handlers, you can use these to dispatch actions to update your Redux store
      connection.on("OnModuleStatusChanged", OnModuleStatusChanged);
      connection.on("OnModuleStateChanged", OnModuleStateChanged);
      connection.on("OnPlateAddressesUpdated", OnPlateAddressesUpdated);
      connection.on("OnTemperatureChanged", OnTemperatureChanged);
      connection.on("OnChillerModuleError", OnChillerModuleError);
      connection.on("OnScheduledPlatesUpdated", OnScheduledPlatesUpdated);
      connection.on("OnQueuedPlatesUpdated", OnQueuedPlatesUpdated);
      connection.on("OnLocateDropCompleted", OnLocateDropCompleted);
      connection.on("OnAutoFocusCompleted", OnAutoFocusCompleted);
      connection.on("OnModuleDiagnosticFinished", OnModuleDiagnosticFinished);
      connection.on("OnHistogramUpdated", OnHistogramUpdated);
      connection.on("NotifyUser", OnNotifyUser);
      connection.on("OnUserLoggedIn", OnUserLoggedIn);
      connection.on("OnLicenseExpired", OnLicenseExpired);

      // re-establish the connection if connection dropped
      connection.onclose(() => {
        console.log("SignalR connection lost");
        setTimeout(async () => {
          startSignalRConnection(true, connection, api.dispatch, () => {
            api.dispatch(fetchStorageConfigs(false) as any);
            api.dispatch(fetchPlateAddresses(false) as any);
          });
        }, 5000);
      });
      startSignalRConnection(true, connection, api.dispatch);
    }
    return next(action);
  };

export default signalRMiddleware;
